import React, { ChangeEvent, ClipboardEvent, Component, FocusEvent, ReactNode, SyntheticEvent } from 'react';
import { sendOutOfFormFlowAnalyticsEvent, sendOutOfFormFlowErrorAnalyticsEvent } from 'services/analytics';
import { AboutYouProps, AboutYouState, BirthDateFieldsValue } from './AboutYou.type';
import { DateFields, Props as DateFieldsProps } from '@nbc-design/date-fields';
import { redirectToUnifiedLogin } from '../../../services/ssoRedirectService';
import { getDateFieldsErrors, getFieldsValidation, getFormFieldErrors } from '../validation';
import { PropType, CreateProfileResponse } from 'types/interfaces';
import AnalyticsHelper from 'services/analytics/AnalyticsHelper';
import { CreateProfileStep, ErrorCode } from '../constant';
import { createUserProfile } from 'services/loginManager';
import { SiteKeyType } from 'components/Captcha/Captcha';
import { saveCaptchaResponse } from 'utils';
import { reactLocalStorage } from 'reactjs-localstorage';
import { sendStepLoadedAnalyticsEvent } from '../utils';
import { FormGroup } from '@nbc-design/form-group';
import LoadingMessenger from './LoadingMessenger';
import { getTextFactory } from 'utils/TextUtils';
import { Fieldset } from '@nbc-design/fieldset';
import { Heading } from '@nbc-design/heading';
import { Col, Row } from 'react-flexbox-grid';
import { Button } from '@nbc-design/button';
import { Input } from '@nbc-design/input';
import { Radio } from '@nbc-design/radio';
import Captcha from 'components/Captcha';
import { Card } from '@nbc-design/card';
import configs from 'configs';
import cx from 'classnames';

type LocaleType = 'fr' | 'en';
type DateFieldsValueOnChange = PropType<Required<Pick<DateFieldsProps, 'onChange'>>, 'onChange'>;

class AboutYou extends Component<AboutYouProps, AboutYouState> {
  state: AboutYouState = {
    formValues: {
      title: '',
      firstName: '',
      middleName: '',
      lastName: '',
      birthDate: '',
    },
    touchedFields: {
      title: false,
      firstName: false,
      middleName: false,
      lastName: false,
      birthDate: false,
    },
    formErrors: { title: '', firstName: '', middleName: '', lastName: '', birthDate: '', connection: '' },
    isSubmitted: false,
    isLoading: false,
    isFormValid: false,
    bncId: 'bncId-mock',
    isProfileSuccessfullyCreated: false,
    dateFieldsCompleted: false,
  };

  hasAnalyticsSent = false;
  spinnerTimeStart = 0;
  redirectTimer: NodeJS.Timeout | null = null;

  getText = getTextFactory(this.props.intl, 'aboutYou');
  getTextError = getTextFactory(this.props.intl, 'error');
  getGlobalText = getTextFactory(this.props.intl, 'global');
  onShowTooltip = AnalyticsHelper.handleShowTooltip('UCS-bao-HD');
  config = {
    ID: 'ABOUT_YOU',
  };

  getCard2Classes = (currentStep: number): string => {
    return cx({ 'spc-creationProfil__card': true, 'is-disabled': currentStep !== 2 });
  };

  componentDidUpdate = () => {
    if (!this.hasAnalyticsSent && this.props.step === CreateProfileStep.PERSONAL_INFO_STEP) {
      sendStepLoadedAnalyticsEvent('PROFILE_PERSONAL_INFO');
      this.hasAnalyticsSent = true;
    }
  };

  componentWillUnmount = () => {
    if (this.redirectTimer) {
      clearTimeout(this.redirectTimer);
      this.redirectTimer = null;
    }
  };

  setError = (name: string, error: string): void => {
    this.setState(({ formErrors }) => ({
      formErrors: {
        ...formErrors,
        [name]: error ? this.getTextError(error) : '',
      },
    }));
  };

  isFormsValid = (): boolean => {
    return this.hasRequiredValues() && !this.formErrorsValues();
  };

  hasRequiredValues = (): boolean => {
    const { middleName, ...requiredValues } = this.state.formValues;
    return Object.values(requiredValues).every((value) => value !== '');
  };

  formErrorsValues = (): string | undefined => {
    return Object.values(this.state.formErrors).find((value) => value !== '');
  };

  validateField = (fieldName: string, value: string): void => {
    const { formErrors } = getFieldsValidation({
      fieldName,
      value,
    });

    this.setError(fieldName, formErrors[fieldName]);
  };

  redirectAfterProfileCreation = (response: CreateProfileResponse | null): void => {
    if (!response?.bncId) {
      return;
    }

    sendStepLoadedAnalyticsEvent('PROFILE_CREATION_CONFIRMATION', response.bncId, undefined, {
      timeSpinner: (new Date().getTime() - this.spinnerTimeStart).toString(),
    });

    this.setState({ bncId: response.bncId, isProfileSuccessfullyCreated: true }, () => {
      this.props.setCardWrapperState({ isSubmitted: true, isError: false });
      this.redirectTimer = setTimeout(() => {
        redirectToUnifiedLogin(this.props.intl.locale, response.sessionToken);
        this.props.setCardWrapperState({
          step: CreateProfileStep.CREDENTIALS_STEP,
          isError: false,
        });
      }, 2000);
    });
  };

  handleCaptchaSuccess = (token: string): void => {
    saveCaptchaResponse(token);
    this.handleFormSubmit();
  };

  executeRecaptcha = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (!configs.params.FEATURE_TOGGLE.IS_RECAPTCHA_ENABLED) {
      this.handleFormSubmit();
      return;
    }
    (window as any).grecaptcha.reset();
    (window as any).grecaptcha.execute();
  };

  handleError = async (error: any): Promise<void> => {
    // email already exists
    if (error?.response?.message?.includes(ErrorCode.BAO_BUS_001)) {
      this.handleEmailAlreadyExists();
      return;
    }

    this.setState({ bncId: this.getText('errorIAMX'), isLoading: false, isProfileSuccessfullyCreated: false }, () => {
      this.props.setCardWrapperState({ step: CreateProfileStep.CREDENTIALS_STEP, isSubmitted: true, isError: true });
    });

    this.setError('connection', 'connection');

    sendOutOfFormFlowAnalyticsEvent('' + this.formErrorsValues(), '' + this.config.ID);
  };

  handleEmailAlreadyExists = () => {
    this.setState({ isLoading: false, isProfileSuccessfullyCreated: false }, () => {
      this.props.setCardWrapperState({
        step: CreateProfileStep.CREDENTIALS_STEP,
        existingEmail: true,
        rejectedEmail: reactLocalStorage.get('userEmail') || '',
        isSubmitted: false,
        isError: false,
      });
    });
  };

  validateAllFields = (
    title: string,
    firstName: string,
    middleName: string,
    lastName: string,
    birthDate: string,
  ): boolean => {
    const fieldErrorKeys = getFormFieldErrors(title, firstName, middleName, lastName, birthDate);
    const translatedErrors = Object.fromEntries(
      Object.entries(fieldErrorKeys).map(([k, v]) => [k, v ? this.getTextError(v) : '']),
    );

    const hasErrors = Object.values(translatedErrors).some((e) => e !== '');
    const { middleName: _middleName, ...requiredValues } = { title, firstName, middleName, lastName, birthDate };
    const isFormValid = Object.values(requiredValues).every((v) => v !== '') && !hasErrors;

    if (hasErrors) {
      sendOutOfFormFlowErrorAnalyticsEvent(translatedErrors, 'personal information');
    }

    this.setState(({ formErrors }) => ({
      formErrors: { ...formErrors, ...translatedErrors },
      isFormValid,
    }));

    return isFormValid;
  };

  handleFormSubmit = (): void => {
    const {
      formValues: { title, firstName, middleName, lastName, birthDate },
    } = this.state;

    const language = this.props.intl.locale.toUpperCase();

    // updates formErrors state for UI display, and returns validity synchronously.
    const isFormValid = this.validateAllFields(title, firstName, middleName, lastName, birthDate);

    this.setState({ isSubmitted: true, isLoading: isFormValid });

    if (isFormValid) {
      this.spinnerTimeStart = new Date().getTime();
      createUserProfile(title, firstName?.trim(), middleName?.trim(), lastName?.trim(), birthDate, language)
        .then(this.redirectAfterProfileCreation)
        .catch(this.handleError);
    }
  };

  onChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const {
      target: { name, value },
    } = event;

    const formValues = this.state.formValues;
    formValues[name] = value.trim();

    this.setState({ formValues }, () => {
      this.state.touchedFields[name] && this.validateField(name, value.trim());
    });
  };

  onDateChange: DateFieldsValueOnChange = (
    value: { day?: string; month?: string; year?: string } = {},
    fullDate,
  ): void => {
    const formValues = this.state.formValues;
    formValues['birthDate'] = this.toISODate(fullDate as BirthDateFieldsValue);

    const touchedFields = this.state.touchedFields;
    touchedFields['birthDate'] = true;

    const dateFieldsCompleted = this.birthDateReadyToValidate(fullDate as BirthDateFieldsValue);

    this.setState({ formValues, touchedFields, dateFieldsCompleted }, () => {
      this.state.touchedFields['birthDate'] &&
        dateFieldsCompleted &&
        this.validateField('birthDate', this.state.formValues.birthDate);
    });
  };

  toISODate = (date: BirthDateFieldsValue = { year: '0000', month: '00', day: '00' }) => {
    return `${date.year.padStart(4)}-${date.month.padStart(2, '0')}-${date.day.padStart(2, '0')}`;
  };

  birthDateReadyToValidate = (date: Required<BirthDateFieldsValue>): boolean => {
    return !!(date.year && date.month && date.day);
  };

  onBlur = (event: FocusEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const {
      target: { name, value },
    } = event;

    const touchedFields = this.state.touchedFields;
    touchedFields[name] = true;

    this.setState({ touchedFields }, () => {
      this.validateField(name, value.trim());
    });
  };

  onPaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault();
  };

  render(): ReactNode {
    const { formErrors, isLoading, isProfileSuccessfullyCreated } = this.state;
    const { step, intl } = this.props;

    return (
      <>
        <Card border="shadow" className={this.getCard2Classes(step)}>
          <form onSubmit={this.executeRecaptcha}>
            <div className="about-you__form">
              <div className="spc-creationProfil__cardBody spc-creationProfil__cardBody--fullContentMobile">
                <div className="spc-creationProfil__stepNumber">2</div>
                <div className="spc-creationProfil__stepTitle">
                  <Heading type="h2" size={5}>
                    {this.getText('stepTitle')}
                  </Heading>
                </div>
                <div></div>
                <div className="spc-creationProfil__cardContent card2">
                  <Row>
                    <Col xs={12} md={12} lg={12}>
                      <Fieldset validate={{ hasError: !!formErrors.title, errorMsg: formErrors.title }}>
                        <div className="radio-inline">
                          <Radio
                            id="radio-title-man"
                            name="title"
                            label={this.getText('titleManLabel')}
                            key="14"
                            value="14"
                            onChange={this.onChange}
                            onBlur={this.onBlur}
                            disabled={isLoading}
                          />
                          <Radio
                            id="radio-title-woman"
                            name="title"
                            label={this.getText('titleWomanLabel')}
                            key="15"
                            value="15"
                            onChange={this.onChange}
                            onBlur={this.onBlur}
                            disabled={isLoading}
                          />
                        </div>
                      </Fieldset>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} md={12} lg={12}>
                      <FormGroup
                        label={{
                          text: this.getText('firstNameLabel'),
                          htmlFor: 'first-name-input',
                        }}
                        validate={{
                          hasError: !!formErrors.firstName,
                          errorMsg: formErrors.firstName,
                        }}
                        data-test="label_firstName"
                        length="large"
                      >
                        <Input
                          id="first-name-input"
                          name="firstName"
                          type="text"
                          onChange={this.onChange}
                          onBlur={this.onBlur}
                          onPaste={this.onPaste}
                          data-test="field_firstName"
                          maxLength={32}
                          disabled={isLoading}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} md={12} lg={12}>
                      <FormGroup
                        label={{
                          text: this.getText('middleNameLabel'),
                          htmlFor: 'middle-name-input',
                        }}
                        validate={{
                          hasError: !!formErrors.middleName,
                          errorMsg: formErrors.middleName,
                        }}
                        optionalText={this.getGlobalText('optional')}
                        data-test="label_middleName"
                        length="large"
                      >
                        <Input
                          id="middle-name-input"
                          name="middleName"
                          type="text"
                          onChange={this.onChange}
                          onBlur={this.onBlur}
                          onPaste={this.onPaste}
                          data-test="field_middleName"
                          maxLength={32}
                          disabled={isLoading}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} md={12} lg={12}>
                      <FormGroup
                        label={{
                          text: this.getText('lastNameLabel'),
                          htmlFor: 'last-name-input',
                        }}
                        validate={{
                          hasError: !!formErrors.lastName,
                          errorMsg: formErrors.lastName,
                        }}
                        data-test="label_lastName"
                        length="large"
                      >
                        <Input
                          id="last-name-input"
                          name="lastName"
                          type="text"
                          onChange={this.onChange}
                          onBlur={this.onBlur}
                          onPaste={this.onPaste}
                          data-test="field_lastName"
                          maxLength={32}
                          disabled={isLoading}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} md={12} lg={12}>
                      <Fieldset
                        legend={this.getText('birthDateLabel')}
                        tooltip={{
                          content: this.getText('tooltipText'),
                          ariaLabel: this.getText('tooltipText'),
                          onShowTooltip: this.onShowTooltip,
                        }}
                        validate={{
                          hasError: !!formErrors.birthDate,
                          errorMsg: formErrors.birthDate,
                        }}
                        data-test="label_birthDate"
                        length="large"
                      >
                        <DateFields
                          id="dateFields-birthDate"
                          locale={this.props.intl.locale as LocaleType}
                          onChange={this.onDateChange}
                          validate={getDateFieldsErrors(
                            this.state.formValues.birthDate,
                            this.state.dateFieldsCompleted,
                          )}
                          disabled={isLoading}
                        />
                      </Fieldset>
                    </Col>
                  </Row>
                </div>
              </div>
              <div className="spc-creationProfil__cardFooter">
                <Button appearance="primary" type="submit">
                  {this.getText('createProfile')}
                </Button>
              </div>
            </div>
          </form>
          {isLoading && <LoadingMessenger intl={intl} isProfileSuccessfullyCreated={isProfileSuccessfullyCreated} />}
        </Card>
        {this.props.step === CreateProfileStep.PERSONAL_INFO_STEP && (
          <Captcha
            siteKeyType={SiteKeyType.frontend}
            success={this.handleCaptchaSuccess}
            timeout={() => {
              // This is intentional
            }}
            failure={() => {
              // This is intentional
            }}
            invisible
          />
        )}
      </>
    );
  }
}

export default AboutYou;
